import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { opportunityAPI, dashboardAPI } from '../../services/api';
import { Opportunity, OpportunityApplication, CreateOpportunityRequest, ApplicationDecisionStatus, Recommendation } from '../../types';
import toast from 'react-hot-toast';

interface OpportunityState {
  opportunities: Opportunity[];
  recommendations: Recommendation[]; // Added
  myOpportunities: Opportunity[];
  currentOpportunity: Opportunity | null;
  applications: OpportunityApplication[];
  myApplications: OpportunityApplication[]; // Added
  currentOpportunityApplications: OpportunityApplication[];
  loading: boolean;
  loadingApplications: boolean; // Add this
  submitting: boolean;
  error: string | null;
  filters: {
    page: number;
    limit: number;
    type?: string;
    location?: string;
    tags?: string;
    search?: string;
    experience?: string;
    compensation?: string;
  };
  pagination: {
    total: number;
    pages: number;
    currentPage: number;
  };
}

const initialState: OpportunityState = {
  opportunities: [],
  recommendations: [], // Added
  myOpportunities: [],
  currentOpportunity: null,
  applications: [],
  myApplications: [], // Added
  currentOpportunityApplications: [],
  loading: false,
  submitting: false,
  loadingApplications: false, // Add this
  error: null,
  filters: {
    page: 1,
    limit: 20,
  },
  pagination: {
    total: 0,
    pages: 0,
    currentPage: 1,
  },
};

// Async thunks
export const fetchOpportunities = createAsyncThunk(
    'opportunities/fetchOpportunities',
    async (params: Partial<typeof initialState.filters> = {}, { rejectWithValue }) => {
      try {
        // Provide defaults for required fields
        const defaultParams = {
          page: 1,
          limit: 20,
          ...params
        };
        
        const response = await opportunityAPI.getOpportunities(defaultParams);
        // console.log(response.data)
        return response.data.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.error || 'Failed to fetch opportunities');
      }
    }
  );
  
export const fetchRecommendations = createAsyncThunk(
  'opportunities/fetchRecommendations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await dashboardAPI.getPersonalizedRecommendations();
      // Backend returns { success: true, data: { data: [...], total: ... } }
      return {
        data: response.data.data.data,
        total: response.data.data.total
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch recommendations');
    }
  }
);

export const fetchOpportunity = createAsyncThunk(
  'opportunities/fetchOpportunity',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await opportunityAPI.getOpportunity(id);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch opportunity');
    }
  }
);

export const createOpportunity = createAsyncThunk(
    'opportunities/createOpportunity',
    async (formData: FormData, { rejectWithValue }) => {
      try {
        const response = await opportunityAPI.createOpportunity(formData);
        return response.data.data.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.error || 'Failed to create opportunity');
      }
    }
  );

  export const updateOpportunity = createAsyncThunk(
    'opportunities/updateOpportunity',
    async ({ id, formData }: { id: string; formData: FormData }, { rejectWithValue }) => {
      try {
        const response = await opportunityAPI.updateOpportunity(id, formData);
        return response.data.data.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.error || 'Failed to update opportunity');
      }
    }
  );
  

export const fetchMyOpportunities = createAsyncThunk(
    'opportunities/fetchMyOpportunities',
    async (status: string = 'all', { rejectWithValue }) => {
      try {
        const response = await opportunityAPI.getMyOpportunities(status === 'all' ? undefined : status);
        // console.log("response", response)
        return response.data.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.error || 'Failed to fetch opportunities');
      }
    }
  );
  

export const applyToOpportunity = createAsyncThunk(
  'opportunities/applyToOpportunity',
  async ({ id, data }: { id: string; data: { message?: string; portfolio_items?: string[] } }, { rejectWithValue }) => {
    try {
      const response = await opportunityAPI.applyToOpportunity(id, data);
      return { opportunityId: id, application: response.data.data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to apply to opportunity');
    }
  }
);

export const withdrawApplication = createAsyncThunk(
  'opportunities/withdrawApplication',
  async ({ opportunityId, userId }: { opportunityId: string; userId: string }, { rejectWithValue }) => {
    try {
      await opportunityAPI.withdrawApplication(opportunityId);
      return { opportunityId, userId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to withdraw application');
    }
  }
);

export const fetchMyApplications = createAsyncThunk(
  'opportunities/fetchMyApplications',
  async (_, { rejectWithValue }) => {
    try {
      // console.log('DEBUG: fetchMyApplications thunk called');
      const response = await opportunityAPI.getMyApplications();
      // console.log('DEBUG: fetchMyApplications response:', response.data);
      return response.data.data;
    } catch (error: any) {
      console.error('DEBUG: fetchMyApplications failed:', error);
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch my applications');
    }
  }
);

// Add these new async thunks to your existing opportunitySlice.ts

export const fetchOpportunityApplications = createAsyncThunk(
    'opportunities/fetchOpportunityApplications',
    async (opportunityId: string, { rejectWithValue }) => {
      try {
        const response = await opportunityAPI.getOpportunityApplications(opportunityId);
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.error || 'Failed to fetch applications');
      }
    }
  );
  
  export const updateApplicationStatus = createAsyncThunk(
    'opportunities/updateApplicationStatus',
    async ({ applicationId, data }: { 
      applicationId: string; 
      data: { status: ApplicationDecisionStatus; message?: string } 
    }, { rejectWithValue }) => {
      try {
        const response = await opportunityAPI.updateApplicationStatus(applicationId, data);
        return response.data.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.error || 'Failed to update application status');
      }
    }
  );
  
  export const bulkRejectApplications = createAsyncThunk(
    'opportunities/bulkRejectApplications',
    async ({ opportunityId, data }: {
      opportunityId: string;
      data: { application_ids: string[]; message?: string }
    }, { rejectWithValue }) => {
      try {
        const response = await opportunityAPI.bulkRejectApplications(opportunityId, data);
        return { opportunityId, rejectedCount: response.data.rejected_count };
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.error || 'Failed to reject applications');
      }
    }
  );

  export const reviveOpportunity = createAsyncThunk(
    'opportunities/reviveOpportunity',
    async (id: string, { rejectWithValue }) => {
      try {
        const response = await opportunityAPI.reviveOpportunity(id);
        return response.data.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.error || 'Failed to revive opportunity');
      }
    }
  );

  export const closeOpportunity = createAsyncThunk(
    'opportunities/closeOpportunity',
    async (id: string, { rejectWithValue }) => {
      try {
        const response = await opportunityAPI.closeOpportunity(id);
        return response.data.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.error || 'Failed to close opportunity');
      }
    }
  );
  

const opportunitySlice = createSlice({
  name: 'opportunities',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<typeof initialState.filters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearCurrentOpportunity: (state) => {
      state.currentOpportunity = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateOpportunityInList: (state, action: PayloadAction<Opportunity>) => {
      const index = state.opportunities.findIndex(opp => opp.id === action.payload.id);
      if (index !== -1) {
        state.opportunities[index] = action.payload;
      }
      
      const myIndex = state.myOpportunities.findIndex(opp => opp.id === action.payload.id);
      if (myIndex !== -1) {
        state.myOpportunities[myIndex] = action.payload;
      }
    },
    clearApplications: (state) => {
        state.currentOpportunityApplications = [];
      },
      updateApplicationInList: (state, action: PayloadAction<OpportunityApplication>) => {
        const index = state.currentOpportunityApplications.findIndex(app => app.id === action.payload.id);
        if (index !== -1) {
          state.currentOpportunityApplications[index] = action.payload;
        }
      },
  },
  extraReducers: (builder) => {
    builder
      // Fetch opportunities
      .addCase(fetchOpportunities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOpportunities.fulfilled, (state, action) => {
        state.loading = false;
        state.opportunities = action.payload.data || [];
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchOpportunities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error('Failed to load opportunities');
      })

      // Fetch recommendations
      .addCase(fetchRecommendations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecommendations.fulfilled, (state, action) => {
        state.loading = false;
        state.recommendations = action.payload.data || [];
        state.pagination.total = action.payload.total || action.payload.data.length || 0;
      })
      .addCase(fetchRecommendations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Don't toast error for recommendations as it might be optional or just empty
        console.error('Failed to load recommendations:', action.payload);
      })
      
      // Fetch single opportunity
      .addCase(fetchOpportunity.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOpportunity.fulfilled, (state, action) => {
        state.loading = false;
        // Backend returns { data: Opportunity, match_info: ... }
        // We merge match_info into the opportunity object
        state.currentOpportunity = {
          ...action.payload.data,
          match_info: action.payload.match_info
        };
      })
      .addCase(fetchOpportunity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Create opportunity
      .addCase(createOpportunity.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(createOpportunity.fulfilled, (state, action) => {
        state.submitting = false;
        state.myOpportunities.unshift(action.payload);
        toast.success('Opportunity posted successfully!');
      })
      .addCase(createOpportunity.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
        toast.error('Failed to post opportunity');
      })
      
      // Fetch my opportunities
      .addCase(fetchMyOpportunities.fulfilled, (state, action) => {
        state.myOpportunities = action.payload;
      })

      // Fetch my applications
      .addCase(fetchMyApplications.fulfilled, (state, action) => {
        state.myApplications = action.payload;
      })
      
      // Apply to opportunity
      .addCase(applyToOpportunity.fulfilled, (state, action) => {
        // Update application count in the opportunity
        const opportunity = state.opportunities.find(opp => opp.id === action.payload.opportunityId);
        if (opportunity) {
          opportunity.application_count += 1;
        }
        
        if (state.currentOpportunity?.id === action.payload.opportunityId) {
          state.currentOpportunity.application_count += 1;
          if (!state.currentOpportunity.applications) {
            state.currentOpportunity.applications = [];
          }
          // Check if application already exists to avoid duplicates
          const exists = state.currentOpportunity.applications.some(app => app.id === action.payload.application.id);
          if (!exists) {
            state.currentOpportunity.applications.push(action.payload.application);
          }
        }
        
        // Add to myApplications with a new array reference
        state.myApplications = [action.payload.application, ...(state.myApplications || [])];
        
        // toast.success('Application submitted successfully!');
      })
      .addCase(applyToOpportunity.rejected, (state, action) => {
        state.error = action.payload as string;
        toast.error(state.error);
      })

      // Withdraw application
      .addCase(withdrawApplication.fulfilled, (state, action) => {
        const { opportunityId, userId } = action.payload;
        
        // Update application count in list
        const opportunity = state.opportunities.find(opp => opp.id === opportunityId);
        if (opportunity) {
          opportunity.application_count = Math.max(0, opportunity.application_count - 1);
        }
        
        // Update current opportunity
        if (state.currentOpportunity?.id === opportunityId) {
          state.currentOpportunity.application_count = Math.max(0, state.currentOpportunity.application_count - 1);
          
          if (state.currentOpportunity.applications) {
            state.currentOpportunity.applications = state.currentOpportunity.applications.filter(
              app => app.applicant_id !== userId
            );
          }
        }
        
        // Remove from myApplications
        state.myApplications = state.myApplications.filter(app => app.opportunity_id !== opportunityId);
        
        toast.success('Application withdrawn successfully');
      })
      .addCase(withdrawApplication.rejected, (state, action) => {
        state.error = action.payload as string;
        toast.error(state.error);
      })

      // Fetch opportunity applications
      .addCase(fetchOpportunityApplications.pending, (state) => {
        state.loadingApplications = true;
        state.error = null;
      })
      .addCase(fetchOpportunityApplications.fulfilled, (state, action) => {
        state.loadingApplications = false;
        state.currentOpportunityApplications = action.payload.data;
      })
      .addCase(fetchOpportunityApplications.rejected, (state, action) => {
        state.loadingApplications = false;
        state.error = action.payload as string;
        toast.error('Failed to load applications');
      })

      // Update application status
      .addCase(updateApplicationStatus.fulfilled, (state, action) => {
        const updatedApplication = action.payload;
        const index = state.currentOpportunityApplications.findIndex(app => app.id === updatedApplication.id);
        if (index !== -1) {
          state.currentOpportunityApplications[index] = updatedApplication;
        }
        toast.success('Application status updated successfully');
      })
      .addCase(updateApplicationStatus.rejected, (state, action) => {
        state.error = action.payload as string;
        toast.error('Failed to update application status');
      })

      // Bulk reject applications
      .addCase(bulkRejectApplications.fulfilled, (state, action) => {
        const { rejectedCount } = action.payload;
        // Update applications in the list to rejected status
        state.currentOpportunityApplications = state.currentOpportunityApplications.map(app =>
          app.status === 'pending'
            ? { ...app, status: 'rejected' as any, updated_at: new Date().toISOString() }
            : app
        );
        toast.success(`Successfully rejected ${rejectedCount} applications`);
      })
      .addCase(bulkRejectApplications.rejected, (state, action) => {
        state.error = action.payload as string;
        toast.error('Failed to reject applications');
      })
      // Update opportunity
      .addCase(updateOpportunity.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(updateOpportunity.fulfilled, (state, action) => {
        state.submitting = false;
        
        // Update the opportunity in the current opportunity state
        if (state.currentOpportunity && state.currentOpportunity.id === action.payload.id) {
          state.currentOpportunity = action.payload;
        }
        
        // Update in opportunities list
        const index = state.opportunities.findIndex(opp => opp.id === action.payload.id);
        if (index !== -1) {
          state.opportunities[index] = action.payload;
        }
        
        // Update in my opportunities list
        const myIndex = state.myOpportunities.findIndex(opp => opp.id === action.payload.id);
        if (myIndex !== -1) {
          state.myOpportunities[myIndex] = action.payload;
        }
        
        toast.success('Opportunity updated successfully');
      })
      .addCase(updateOpportunity.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
        // toast.error('Failed to update opportunity');
      })

      // Revive opportunity
      .addCase(reviveOpportunity.fulfilled, (state, action) => {
        const revivedOpp = action.payload.data;
        
        // Update in opportunities list
        const index = state.opportunities.findIndex(opp => opp.id === revivedOpp.id);
        if (index !== -1) {
          state.opportunities[index] = revivedOpp;
        }
        
        // Update in my opportunities list
        const myIndex = state.myOpportunities.findIndex(opp => opp.id === revivedOpp.id);
        if (myIndex !== -1) {
          state.myOpportunities[myIndex] = revivedOpp;
        }
        
        toast.success('Opportunity revived successfully');
      })
      .addCase(reviveOpportunity.rejected, (state, action) => {
        state.error = action.payload as string;
        toast.error('Failed to revive opportunity');
      })
      
      // Close opportunity
      .addCase(closeOpportunity.fulfilled, (state, action) => {
        const closedOpp = action.payload.data;
        
        // Update in opportunities list
        const index = state.opportunities.findIndex(opp => opp.id === closedOpp.id);
        if (index !== -1) {
          state.opportunities[index] = closedOpp;
        }
        
        // Update in my opportunities list
        const myIndex = state.myOpportunities.findIndex(opp => opp.id === closedOpp.id);
        if (myIndex !== -1) {
          state.myOpportunities[myIndex] = closedOpp;
        }
        
        toast.success('Opportunity closed successfully');
      })
      .addCase(closeOpportunity.rejected, (state, action) => {
        state.error = action.payload as string;
        toast.error('Failed to close opportunity');
      });
      
  },
});

export const { 
    setFilters, 
    clearCurrentOpportunity, 
    clearError, 
    updateOpportunityInList,
    clearApplications, // Add this
    updateApplicationInList, // Add this 
} = opportunitySlice.actions;
export default opportunitySlice.reducer;
